import { useEffect, useMemo, useState } from 'preact/hooks';
import { currentBible, setBible, setCompareBible, compareBible } from '../state/bible-store.js';
import {
  fetchBiblesForLanguage,
  fetchLanguages,
  type Bible,
  type Language,
} from '../audio/bb-catalog.js';

type Props = {
  onClose: () => void;
  /** 'main' (default) writes to currentBible; 'compare' writes to compareBible. */
  mode?: 'main' | 'compare';
};

type Step = 'languages' | 'bibles';

export function BiblePicker({ onClose, mode = 'main' }: Props) {
  const [step, setStep] = useState<Step>('languages');
  const [languages, setLanguages] = useState<Language[] | null>(null);
  const [bibles, setBibles] = useState<Bible[] | null>(null);
  const [pickedLang, setPickedLang] = useState<Language | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    fetchLanguages()
      .then((langs) => {
        if (!alive) return;
        if (langs.length === 0) {
          setError('Languages unavailable — Bible Brain key not configured.');
        }
        setLanguages(langs);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Failed to load languages.');
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (step !== 'bibles' || !pickedLang) return;
    let alive = true;
    setBibles(null);
    fetchBiblesForLanguage(pickedLang.iso)
      .then((list) => {
        if (alive) setBibles(list);
      })
      .catch((err) => {
        if (alive) setError(err instanceof Error ? err.message : 'Failed to load bibles.');
      });
    return () => {
      alive = false;
    };
  }, [step, pickedLang]);

  const filtered = useMemo(() => {
    if (!languages) return null;
    const q = query.trim().toLowerCase();
    if (!q) return languages;
    return languages.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        (l.autonym ?? '').toLowerCase().includes(q) ||
        l.iso.toLowerCase().includes(q),
    );
  }, [languages, query]);

  const active = mode === 'compare' ? compareBible.value ?? currentBible.value : currentBible.value;

  return (
    <div class="bible-picker-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div class="bible-picker" onClick={(e) => e.stopPropagation()}>
        <header class="bible-picker-header">
          {step === 'bibles' && pickedLang ? (
            <button
              type="button"
              class="bible-picker-back"
              onClick={() => {
                setStep('languages');
                setPickedLang(null);
              }}
              aria-label="Back to languages"
            >
              ←
            </button>
          ) : null}
          <span class="bible-picker-title">
            {mode === 'compare' && step === 'languages'
              ? 'Compare with…'
              : step === 'languages'
                ? 'Choose language'
                : pickedLang?.name ?? 'Choose translation'}
          </span>
          <button
            type="button"
            class="bible-picker-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error ? <p class="bible-picker-error">{error}</p> : null}

        {step === 'languages' ? (
          <>
            <div class="bible-picker-search-row">
              <input
                type="search"
                class="bible-picker-search"
                placeholder="Search languages…"
                value={query}
                onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
                autoFocus
              />
            </div>
            <div class="bible-picker-body">
              {!languages ? (
                <p class="bible-picker-empty">Loading languages…</p>
              ) : filtered && filtered.length > 0 ? (
                <ul class="bible-picker-list">
                  {filtered.map((lang) => (
                    <li key={lang.iso}>
                      <button
                        type="button"
                        class={`bible-picker-row${lang.iso === active.languageIso ? ' is-active' : ''}`}
                        onClick={() => {
                          setPickedLang(lang);
                          setStep('bibles');
                        }}
                      >
                        <span class="bible-picker-row-main">{lang.name}</span>
                        {lang.autonym && lang.autonym !== lang.name ? (
                          <span class="bible-picker-row-sub">{lang.autonym}</span>
                        ) : null}
                        <span class="bible-picker-row-meta">{lang.bibles} bibles</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p class="bible-picker-empty">No languages match "{query}".</p>
              )}
            </div>
          </>
        ) : (
          <div class="bible-picker-body">
            {!bibles ? (
              <p class="bible-picker-empty">Loading translations…</p>
            ) : bibles.length === 0 ? (
              <p class="bible-picker-empty">No translations available.</p>
            ) : (
              <ul class="bible-picker-list">
                {bibles.map((bible) => {
                  const isActive = bible.abbr === active.bibleId;
                  const displayName = bible.vname ?? bible.name;
                  return (
                    <li key={bible.abbr}>
                      <button
                        type="button"
                        class={`bible-picker-row${isActive ? ' is-active' : ''}`}
                        disabled={!bible.hasText}
                        onClick={() => {
                          const selection = {
                            bibleId: bible.abbr,
                            languageIso: bible.iso,
                            displayName,
                            hasOT: bible.hasOT,
                            hasNT: bible.hasNT,
                          };
                          if (mode === 'compare') setCompareBible(selection);
                          else setBible(selection);
                          onClose();
                        }}
                      >
                        <span class="bible-picker-row-main">{displayName}</span>
                        <span class="bible-picker-row-sub">{bible.abbr}</span>
                        {!bible.hasText ? (
                          <span class="bible-picker-row-meta">audio only</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
